!function () {
  'use strict';

  if (customElements.get('bundle-builder')) return;

  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      connectedCallback() {
        const maxProductsRaw = this.dataset.maxProducts;
        this.maxProducts =
          maxProductsRaw !== undefined && maxProductsRaw !== ''
            ? parseInt(maxProductsRaw, 10)
            : 4;
        this.bundleDiscount = this.parseBundleDiscount(
          JSON.parse(this.dataset.bundleDiscount || '{"quantity":2,"discount":25}')
        );
        this.bundleLabel = this.dataset.bundleLabel || 'Custom Bundle';
        this.bundleImage = this.dataset.bundleImage || '';
        this.sectionId = this.dataset.sectionId || '';
        this.displaySlotCount = this.maxProducts;
        this.showBundleCount = this.dataset.showBundleCount === 'true';
        this.showAllTab = this.dataset.showAllTab === 'true';

        this.selectedProducts = [];
        this.activeCollection = this.dataset.defaultFilter || 'all';
        this.activeShade = 'all';
        this.isLoading = false;

        this.filterTabs = this.querySelectorAll('[data-bundle-filter-handle]');
        this.shadeFiltersRoot = this.querySelector('[data-bundle-shade-filters]');
        this.shadePanels = this.querySelectorAll('[data-bundle-shade-group]');
        this.groups = this.querySelectorAll('[data-bundle-group]');
        this.productCards = this.querySelectorAll('[data-bundle-product]');

        this.bindEvents();
        this.setActiveCollection(this.activeCollection);
        this.updateUI();
      }

      bindEvents() {
        this.addEventListener('click', (event) => {
          const filterBtn = event.target.closest('[data-bundle-filter-handle]');
          if (filterBtn) {
            this.setActiveCollection(filterBtn.dataset.bundleFilterHandle);
            return;
          }

          const shadeBtn = event.target.closest('[data-bundle-shade-handle]');
          if (shadeBtn) {
            this.setActiveShade(shadeBtn.dataset.bundleShadeHandle);
            return;
          }

          const addBtn = event.target.closest('[data-bundle-add]');
          if (addBtn) {
            const card = addBtn.closest('[data-bundle-product]');
            if (card) this.addToBundle(this.getProductFromCard(card));
            return;
          }

          const removeBtn = event.target.closest('[data-bundle-remove]');
          if (removeBtn) {
            const index = parseInt(removeBtn.dataset.bundleRemove, 10);
            if (!Number.isNaN(index)) this.removeFromBundle(index);
            return;
          }

          const atcBtn = event.target.closest('[data-bundle-atc]');
          if (atcBtn) {
            this.addBundleToCart();
            return;
          }
        });
      }

      getProductFromCard(card) {
        return {
          id: parseInt(card.dataset.productId, 10),
          variant_id: parseInt(card.dataset.variantId, 10),
          price: parseInt(card.dataset.price, 10),
          compare_at_price: parseInt(card.dataset.compareAtPrice, 10),
          title: card.dataset.title,
          url: card.dataset.url,
          featured_image: card.dataset.image,
          element: card,
        };
      }

      get bundleTotal() {
        return this.selectedProducts.reduce((sum, p) => sum + p.price, 0);
      }

      parseBundleDiscount(raw) {
        const tier1Quantity = Number(raw?.quantity) || 2;
        const tier1Discount = raw?.discount != null ? Number(raw.discount) : 25;

        const tier2Raw = raw?.tier2;
        let tier2 = null;

        if (tier2Raw?.enabled) {
          const tier2Quantity = Number(tier2Raw.quantity);
          const tier2Discount = tier2Raw.discount != null ? Number(tier2Raw.discount) : 0;

          if (tier2Quantity > tier1Quantity) {
            tier2 = {
              enabled: true,
              quantity: tier2Quantity,
              discount: tier2Discount,
            };
          }
        }

        return {
          quantity: tier1Quantity,
          discount: tier1Discount,
          tier2,
        };
      }

      getDiscountTiers() {
        const tiers = [
          { index: 1, quantity: this.bundleDiscount.quantity, discount: this.bundleDiscount.discount },
        ];

        if (this.bundleDiscount.tier2) {
          tiers.push({
            index: 2,
            quantity: this.bundleDiscount.tier2.quantity,
            discount: this.bundleDiscount.tier2.discount,
          });
        }

        return tiers;
      }

      getActiveDiscount() {
        const count = this.selectedProducts.length;
        let active = null;

        this.getDiscountTiers().forEach((tier) => {
          if (count >= tier.quantity) active = tier;
        });

        return active;
      }

      get discountAmount() {
        return this.getLineDiscountAllocations().reduce((sum, discount) => sum + discount, 0);
      }

      get finalPrice() {
        const allocations = this.getLineDiscountAllocations();
        return this.selectedProducts.reduce(
          (sum, product, index) => sum + product.price - allocations[index],
          0
        );
      }

      getLineDiscountAllocations() {
        const activeDiscount = this.getActiveDiscount();
        const cacheKey = [
          this.selectedProducts.length,
          this.bundleTotal,
          activeDiscount?.discount ?? 0,
        ].join(':');

        if (this._lineDiscountCacheKey === cacheKey && this._lineDiscountCache) {
          return this._lineDiscountCache;
        }

        const allocations = !activeDiscount
          ? this.selectedProducts.map(() => 0)
          : this.selectedProducts.map((product) =>
              Math.round((product.price * activeDiscount.discount) / 100)
            );

        this._lineDiscountCacheKey = cacheKey;
        this._lineDiscountCache = allocations;
        return allocations;
      }

      get isAtMax() {
        return this.selectedProducts.length >= this.maxProducts;
      }

      get isComplete() {
        return this.selectedProducts.length > 0;
      }

      formatMoney(cents) {
        if (cents === 0) return window.theme?.strings?.free || 'Free';
        return window.theme.formatMoney(cents, window.theme.moneyFormat);
      }

      formatPrice(cents) {
        return window.theme.formatMoney(cents, window.theme.moneyFormat);
      }

      getProductDiscountedPrice(product) {
        const index = this.selectedProducts.indexOf(product);
        if (index === -1) return product.price;

        const allocations = this.getLineDiscountAllocations();
        return product.price - allocations[index];
      }

      getBundleCountLabel() {
        const count = this.selectedProducts.length;
        return ` (${count}/${this.maxProducts})`;
      }

      shouldShowSlotTier(slotIndex, tierIndex) {
        const slotNumber = slotIndex + 1;
        const tier1Quantity = this.bundleDiscount.quantity;
        const tier2 = this.bundleDiscount.tier2;

        if (tierIndex === 1) {
          if (slotNumber < tier1Quantity) return false;
          if (tier2 && slotNumber >= tier2.quantity) return false;
          return true;
        }

        if (tierIndex === 2) {
          return !!(tier2 && slotNumber >= tier2.quantity);
        }

        return false;
      }

      getSlotTierLabel(slotIndex, isFilled, tierIndex) {
        if (!this.shouldShowSlotTier(slotIndex, tierIndex)) return null;

        const tier = this.getDiscountTiers().find((entry) => entry.index === tierIndex);
        if (!tier) return null;

        const activeDiscount = this.getActiveDiscount();

        if (isFilled) {
          if (!activeDiscount) return null;
          return `+ ${Math.round(activeDiscount.discount)}% OFF`;
        }

        return `Unlock ${Math.round(tier.discount)}% OFF`;
      }

      updateSlotTierLabel(slotEl, index) {
        const isFilled = !!this.selectedProducts[index];

        slotEl.querySelectorAll('[data-bundle-slot-tier]').forEach((tierEl) => {
          const tierIndex = parseInt(tierEl.dataset.tierIndex, 10);
          const label = this.getSlotTierLabel(index, isFilled, tierIndex);
          const textEl = tierEl.querySelector('[data-bundle-slot-tier-text]');

          if (label) {
            tierEl.hidden = false;
            if (textEl) textEl.textContent = label;
          } else {
            tierEl.hidden = true;
            if (textEl) textEl.textContent = '';
          }
        });
      }

      setActiveCollection(handle) {
        this.activeCollection = handle;

        this.filterTabs.forEach((tab) => {
          const isActive = tab.dataset.bundleFilterHandle === handle;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.groups.forEach((group) => {
          const groupHandle = group.dataset.bundleGroup;
          group.hidden = handle !== 'all' && groupHandle !== handle;
        });

        this.classList.toggle('is-filtered', handle !== 'all');
        this.updateShadeFilters();
        this.applyShadeFilter();
      }

      getNextCollectionHandle() {
        const handles = [...this.filterTabs]
          .map((tab) => tab.dataset.bundleFilterHandle)
          .filter((handle) => handle && handle !== 'all');
        const currentIndex = handles.indexOf(this.activeCollection);
        if (currentIndex === -1 || currentIndex === handles.length - 1) return null;
        return handles[currentIndex + 1];
      }

      advanceToNextCollection() {
        if (this.isAtMax || this.activeCollection === 'all') return;

        const nextHandle = this.getNextCollectionHandle();
        if (!nextHandle) return;

        this.setActiveCollection(nextHandle);

        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      getDefaultShadeHandle(panel) {
        if (!panel) return 'all';
        if (this.showAllTab) return 'all';

        const firstShadeTab = panel.querySelector(
          '[data-bundle-shade-handle]:not([data-bundle-shade-handle="all"])'
        );

        return firstShadeTab?.dataset.bundleShadeHandle || 'all';
      }

      updateShadeFilters() {
        if (!this.shadeFiltersRoot) return;

        if (this.activeCollection === 'all') {
          this.activeShade = 'all';
          this.shadePanels.forEach((panel) => {
            panel.hidden = true;
          });
          this.shadeFiltersRoot.hidden = true;
          return;
        }

        const panelKey = this.activeCollection;
        let activePanel = null;

        this.shadePanels.forEach((panel) => {
          const isMatch = panel.dataset.bundleShadeGroup === panelKey;
          panel.hidden = !isMatch;
          if (isMatch && panel.dataset.hasShades === 'true') {
            activePanel = panel;
          }
        });

        this.shadeFiltersRoot.hidden = !activePanel;

        if (!activePanel) {
          this.activeShade = 'all';
          return;
        }

        this.activeShade = this.getDefaultShadeHandle(activePanel);

        activePanel.querySelectorAll('[data-bundle-shade-handle]').forEach((tab) => {
          const isActive = tab.dataset.bundleShadeHandle === this.activeShade;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      setActiveShade(handle) {
        this.activeShade = handle;

        const panel = this.querySelector(
          `[data-bundle-shade-group="${this.activeCollection}"]:not([hidden])`
        );
        if (!panel) return;

        panel.querySelectorAll('[data-bundle-shade-handle]').forEach((tab) => {
          const isActive = tab.dataset.bundleShadeHandle === handle;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.applyShadeFilter();
      }

      applyShadeFilter() {
        this.querySelectorAll('[data-bundle-group]:not([hidden])').forEach((group) => {
          group.querySelectorAll('[data-bundle-product]').forEach((card) => {
            if (this.activeShade === 'all') {
              card.hidden = false;
              return;
            }

            const shades = (card.dataset.bundleShades || '').split(',').filter(Boolean);
            card.hidden = !shades.includes(this.activeShade);
          });
        });

        this.animateVisibleProductCards();
      }

      animateVisibleProductCards() {
        const cards = this.querySelectorAll(
          '[data-bundle-group]:not([hidden]) [data-bundle-product]:not([hidden])'
        );

        this.querySelectorAll('[data-bundle-product].is-entering').forEach((card) => {
          card.classList.remove('is-entering');
        });

        void this.offsetWidth;

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.classList.add('is-cards-ready');
          return;
        }

        cards.forEach((card) => {
          card.classList.add('is-entering');
        });

        this.classList.add('is-cards-ready');
      }

      isProductSelected(productId) {
        return this.selectedProducts.some((p) => p.id === productId);
      }

      addToBundle(product) {
        if (this.isProductSelected(product.id) || this.isAtMax) return;
        this.selectedProducts.push(product);
        this.updateUI();
        this.advanceToNextCollection();
      }

      removeFromBundle(index) {
        this.selectedProducts.splice(index, 1);
        this.updateUI();
      }

      getBundleLineProperties(bundleId) {
        const activeDiscount = this.getActiveDiscount();
        const properties = {
          Bundle: this.bundleLabel,
          _bundle_title: this.bundleLabel,
          _bundle_id: bundleId,
          _bundle_builder: 'byob',
          _bundle_section_id: this.sectionId,
          _bundle_discount_quantity: String(this.bundleDiscount.quantity),
          _bundle_discount_percent: String(this.bundleDiscount.discount),
          _bundle_item_count: String(this.selectedProducts.length),
          _bundle_subtotal_cents: String(this.bundleTotal),
          _bundle_discount_cents: String(this.discountAmount),
          _bundle_total_cents: String(this.finalPrice),
        };

        if (this.bundleDiscount.tier2) {
          properties._bundle_discount_tier2_quantity = String(this.bundleDiscount.tier2.quantity);
          properties._bundle_discount_tier2_percent = String(this.bundleDiscount.tier2.discount);
        }

        if (activeDiscount) {
          properties._bundle_active_discount_percent = String(activeDiscount.discount);
        }

        if (this.bundleImage) {
          properties._bundle_image = this.bundleImage;
        }

        return properties;
      }

      addBundleToCart() {
        if (!this.isComplete || this.isLoading) return;

        this.isLoading = true;
        this.updateAtcButtons();

        const bundleId = `bundle_${Date.now()}`;
        const bundleProperties = this.getBundleLineProperties(bundleId);
        const items = this.selectedProducts.map((product) => ({
          id: product.variant_id,
          quantity: 1,
          properties: { ...bundleProperties },
        }));

        const cartAddUrl = `${window.theme.routes.cart_add_url}.js`;

        fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        })
          .then(async (response) => {
            const data = await response.json().catch(() => ({}));

            if (!response.ok || data.status) {
              const errorMessage =
                data.description || data.message || 'Failed to add bundle to cart';
              console.error('Cart error:', errorMessage, data);
              alert(errorMessage);
              return;
            }

            if (window.theme.settings.cartType === 'page') {
              window.location = window.theme.routes.cart_url;
              return;
            }

            document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));

            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer) {
              cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show', { bubbles: true }));
            }

            this.selectedProducts = [];
            this.updateUI();
          })
          .catch((error) => {
            console.error('Error adding bundle to cart:', error);
            alert('There was an error adding the bundle to your cart. Please try again.');
          })
          .finally(() => {
            this.isLoading = false;
            this.updateAtcButtons();
          });
      }

      updateProductCards() {
        this.productCards.forEach((card) => {
          const productId = parseInt(card.dataset.productId, 10);
          const isSelected = this.isProductSelected(productId);
          const price = parseInt(card.dataset.price, 10);
          const compareAt = parseInt(card.dataset.compareAtPrice, 10);
          const priceEl = card.querySelector('[data-bundle-card-price]');
          let btn = card.querySelector('.bundle-builder__card-btn');

          card.classList.toggle('is-selected', isSelected);

          if (!btn) return;

          const newBtn = btn.cloneNode(false);
          newBtn.className = 'btn btn--outline btn--secondary bundle-builder__card-btn';

          if (isSelected) {
            newBtn.textContent = 'Added';
            newBtn.setAttribute('aria-pressed', 'true');
            newBtn.setAttribute('aria-label', 'Remove from bundle');
            newBtn.setAttribute('data-bundle-remove', this.selectedProducts.findIndex((p) => p.id === productId));
            newBtn.removeAttribute('data-bundle-add');
          } else {
            const atMax = this.isAtMax;
            newBtn.textContent = atMax ? 'Bundle full' : 'Add to bundle';
            newBtn.disabled = atMax;
            newBtn.setAttribute('aria-pressed', 'false');
            newBtn.setAttribute('data-bundle-add', productId);
            newBtn.removeAttribute('data-bundle-remove');
            newBtn.removeAttribute('aria-label');
          }

          btn.replaceWith(newBtn);

          if (priceEl) {
            if (isSelected && this.discountAmount > 0) {
              const selectedProduct = this.selectedProducts.find((p) => p.id === productId);
              priceEl.innerHTML = `<s>${this.formatMoney(price)}</s> ${this.formatMoney(
                selectedProduct ? this.getProductDiscountedPrice(selectedProduct) : price
              )}`;
            } else if (isSelected && compareAt > price) {
              priceEl.innerHTML = `<s>${this.formatMoney(compareAt)}</s> ${this.formatMoney(price)}`;
            } else {
              priceEl.textContent = this.formatMoney(price);
            }
          }
        });
      }

      updateSlotContainer(container) {
        if (!container) return;

        const slotsRoot = container.querySelector('[data-bundle-slots]');
        if (!slotsRoot) return;

        const baseSlots = slotsRoot.querySelectorAll('[data-bundle-slot]:not([data-bundle-slot-additional])');

        baseSlots.forEach((slotEl, index) => {
          const product = this.selectedProducts[index];
          this.populateSlot(slotEl, product, index);
        });

        container.querySelector('[data-bundle-additional-slots]')?.replaceChildren();

        this.updatePlaceholderLineStates(container);
      }

      updatePlaceholderLineStates(container) {
        const placeholders = container.querySelectorAll('[data-bundle-slot]');

        placeholders.forEach((slotEl, index) => {
          const hasProduct = !!this.selectedProducts[index];
          slotEl.classList.toggle('is-filled', hasProduct);
        });

        this.ensurePlaceholderLineObserver();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.updatePlaceholderLines(container);
            window.setTimeout(() => this.updatePlaceholderLines(container), 220);
          });
        });
      }

      ensurePlaceholderLineObserver() {
        if (this._placeholderLineObserver) return;

        this._placeholderLineObserver = new ResizeObserver(() => {
          const summary = this.querySelector('[data-bundle-summary]');
          if (summary) this.updatePlaceholderLines(summary);
        });

        const slotsRoot = this.querySelector('[data-bundle-summary] [data-bundle-slots]');
        if (!slotsRoot) return;

        this._placeholderLineObserver.observe(slotsRoot);
        slotsRoot.querySelectorAll('[data-bundle-slot]').forEach((slot) => {
          this._placeholderLineObserver.observe(slot);
        });
      }

      updatePlaceholderLines(container) {
        if (!container || !container.offsetParent) return;

        const slotsRoot = container.querySelector('[data-bundle-slots]');
        if (!slotsRoot) return;

        const trackLine = slotsRoot.querySelector('[data-bundle-track-line]');
        if (!trackLine) return;

        const placeholders = slotsRoot.querySelectorAll('[data-bundle-slot]');
        if (placeholders.length < 2) {
          trackLine.hidden = true;
          return;
        }

        const firstDot = placeholders[0].querySelector('.bundle-builder__placeholder-dot');
        const lastDot = placeholders[placeholders.length - 1].querySelector('.bundle-builder__placeholder-dot');
        if (!firstDot || !lastDot) return;

        const rootRect = slotsRoot.getBoundingClientRect();
        const firstRect = firstDot.getBoundingClientRect();
        const lastRect = lastDot.getBoundingClientRect();

        const lineTop = firstRect.top + firstRect.height / 2 - rootRect.top;
        const lineEnd = lastRect.top + lastRect.height / 2 - rootRect.top;
        const lineHeight = Math.max(0, lineEnd - lineTop);

        trackLine.hidden = lineHeight === 0;
        trackLine.style.top = `${lineTop}px`;
        trackLine.style.height = `${lineHeight}px`;
      }

      populateSlot(slotEl, product, index) {
        const emptyEl = slotEl.querySelector('[data-bundle-slot-empty]');
        const filledEl = slotEl.querySelector('[data-bundle-slot-filled]');
        const removeBtn = slotEl.querySelector('[data-bundle-remove]');
        const linkEls = slotEl.querySelectorAll('[data-bundle-slot-link], [data-bundle-slot-info-link]');

        slotEl.dataset.bundleSlot = index;

        if (!product) {
          if (emptyEl) emptyEl.hidden = false;
          if (filledEl) filledEl.hidden = true;
          slotEl.classList.remove('is-filled');
          this.updateSlotTierLabel(slotEl, index);
          return;
        }

        if (emptyEl) emptyEl.hidden = true;
        if (filledEl) filledEl.hidden = false;

        const imageEl = slotEl.querySelector('[data-bundle-slot-image]');
        const titleEl = slotEl.querySelector('[data-bundle-slot-title]');
        const priceEl = slotEl.querySelector('[data-bundle-slot-price]');

        if (imageEl) {
          imageEl.src = product.featured_image;
          imageEl.alt = product.title;
        }

        linkEls.forEach((link) => {
          link.href = product.url;
        });

        if (titleEl) {
          titleEl.textContent = '';
          const strong = document.createElement('strong');
          strong.textContent = product.title;
          titleEl.appendChild(strong);
        }

        if (priceEl) {
          if (this.discountAmount > 0) {
            priceEl.innerHTML = `${this.formatMoney(this.getProductDiscountedPrice(product))} <s>${this.formatMoney(
              product.price
            )}</s>`;
          } else {
            priceEl.textContent = this.formatMoney(product.price);
          }
        }

        if (removeBtn) {
          removeBtn.dataset.bundleRemove = index;
        }

        this.updateSlotTierLabel(slotEl, index);
      }

      updatePricing() {
        this.querySelectorAll('[data-bundle-subtotal]').forEach((el) => {
          el.textContent = this.formatPrice(this.bundleTotal);
        });

        this.querySelectorAll('[data-bundle-savings]').forEach((el) => {
          const savings = this.formatPrice(this.discountAmount);
          el.textContent = this.discountAmount > 0 ? `- ${savings}` : savings;
        });

        this.querySelectorAll('[data-bundle-total]').forEach((el) => {
          el.textContent = this.formatPrice(this.finalPrice);
        });
      }

      updateCountLabels() {
        if (!this.showBundleCount) return;

        const label = this.getBundleCountLabel();
        this.querySelectorAll('[data-bundle-count-label]').forEach((el) => {
          el.textContent = label;
        });
      }

      updateAtcButtons() {
        const disabled = !this.isComplete || this.isLoading;

        this.querySelectorAll('[data-bundle-atc]').forEach((btn) => {
          btn.disabled = disabled;
          btn.classList.toggle('is-loading', this.isLoading);
        });

        this.querySelectorAll('[data-bundle-atc-text]').forEach((el) => {
          el.textContent = this.isLoading ? 'Adding...' : 'Add bundle to cart';
        });
      }

      updateUI() {
        this.updateProductCards();
        this.updateSlotContainer(this.querySelector('[data-bundle-summary]'));
        this.updateSlotContainer(this.querySelector('[data-bundle-mobile]'));
        this.updatePricing();
        this.updateCountLabels();
        this.updateAtcButtons();
      }
    }
  );
}();
